/**
 * oneat-header.js
 * -----------------------------------------------------------------
 * Behaviour for sections/oneat-header.liquid:
 *   - categories mega panel (click to toggle, closes on outside click / Esc)
 *   - predictive search into #oh-results-* via the Section Rendering API
 *   - mobile search row + drawer with accordion sub-menus
 *   - cart badge kept in sync with Horizon's `cart:update` event
 *
 * Plain script (not an @theme module) so it can be loaded with `defer`
 * straight from the section, matching the other custom sections.
 */
(function () {
  'use strict';

  var DEBOUNCE = 260;
  var MIN_QUERY = 2;

  function init(root) {
    if (root.dataset.ohReady === 'true') return;
    root.dataset.ohReady = 'true';

    var predictiveSection = root.dataset.predictiveSection || 'ch-predictive-search';
    var searchUrl = root.dataset.searchUrl || '/search';
    var results = root.querySelector('[data-oh-results]');
    var drawer = root.querySelector('[data-oh-drawer]');
    var scrim = root.querySelector('.oh-scrim');
    var msearch = root.querySelector('.oh-msearch');
    var msearchToggle = root.querySelector('[data-oh-msearch-toggle]');
    var openPanel = null;

    /* ── submenus ───────────────────────────────────────
       Opening and closing is driven entirely by CSS :hover / :focus-within
       so the wipe matches the stock header's timing exactly. All this does
       is mirror the state onto aria-expanded, using the same close delay
       the CSS uses, and give Escape a way out. */

    var subDelay = 140; // matches --oh-sub-delay / the old PANEL_CLOSE_DELAY
    var subTimers = new WeakMap();

    function setExpanded(item, expanded) {
      var link = item.querySelector('.oh-nav__link');
      if (link) link.setAttribute('aria-expanded', String(expanded));
    }

    root.querySelectorAll('[data-oh-has-sub]').forEach(function (item) {
      function open() {
        var timer = subTimers.get(item);
        if (timer) {
          clearTimeout(timer);
          subTimers.delete(item);
        }
        setExpanded(item, true);
      }

      function close() {
        var timer = subTimers.get(item);
        if (timer) clearTimeout(timer);
        subTimers.set(
          item,
          window.setTimeout(function () {
            subTimers.delete(item);
            setExpanded(item, false);
          }, subDelay)
        );
      }

      item.addEventListener('pointerenter', function () {
        open();
        // Drop any sibling panel at once instead of letting it fade out
        // across the close delay and overlap this one.
        root.querySelectorAll('[data-oh-has-sub]').forEach(function (other) {
          if (other === item) return;
          var sub = other.querySelector('.oh-sub');
          if (!sub) return;
          sub.classList.add('oh-sub--instant');
          window.setTimeout(function () {
            sub.classList.remove('oh-sub--instant');
          }, 50);
        });
      });
      item.addEventListener('pointerleave', close);
      item.addEventListener('focusin', open);
      item.addEventListener('focusout', function (event) {
        if (event.relatedTarget && item.contains(event.relatedTarget)) return;
        close();
      });
    });

    /* ── results panel ──────────────────────────────────── */

    function showPanel(panel) {
      if (openPanel === panel) return;
      if (openPanel) hidePanel();
      panel.hidden = false;
      // next frame so the transition runs from the hidden state
      requestAnimationFrame(function () {
        panel.classList.add('is-open');
      });
      openPanel = panel;
    }

    function hidePanel() {
      if (!openPanel) return;
      var panel = openPanel;
      openPanel = null;
      panel.classList.remove('is-open');
      window.setTimeout(function () {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, 220);
    }

    document.addEventListener('click', function (event) {
      if (!openPanel) return;
      if (root.contains(event.target)) return;
      hidePanel();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      hidePanel();
      closeDrawer();
      // CSS keeps a submenu open while it is hovered or focused; dropping
      // focus is the only way to close one from the keyboard.
      var active = document.activeElement;
      if (active instanceof HTMLElement && root.contains(active)) active.blur();
    });

    /* ── predictive search ──────────────────────────────── */

    var timer = null;
    var controller = null;
    var lastQuery = '';

    function renderStatus(message) {
      if (!results) return;
      results.innerHTML = '<p class="oh-results__status">' + message + '</p>';
    }

    function fetchResults(query) {
      if (!results) return;
      if (controller) controller.abort();
      controller = new AbortController();

      var url =
        '/search/suggest?q=' +
        encodeURIComponent(query) +
        '&section_id=' +
        encodeURIComponent(predictiveSection) +
        '&resources[type]=product,collection,query&resources[limit]=6';

      fetch(url, { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var body = doc.querySelector('.ch-psr');
          results.innerHTML = body ? body.innerHTML : '';
          if (!results.textContent.trim()) {
            renderStatus('No results for “' + query + '”');
          }
          showPanel(results);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          renderStatus('Search is unavailable right now.');
          showPanel(results);
        });
    }

    root.querySelectorAll('[data-oh-search-input]').forEach(function (input) {
      input.addEventListener('input', function () {
        var query = input.value.trim();
        window.clearTimeout(timer);

        if (query.length < MIN_QUERY) {
          lastQuery = '';
          if (openPanel === results) hidePanel();
          return;
        }
        if (query === lastQuery) return;

        timer = window.setTimeout(function () {
          lastQuery = query;
          fetchResults(query);
        }, DEBOUNCE);
      });

      input.addEventListener('focus', function () {
        if (input.value.trim().length >= MIN_QUERY && results && results.innerHTML.trim()) {
          showPanel(results);
        }
      });
    });

    root.querySelectorAll('[data-oh-search-form]').forEach(function (form) {
      form.addEventListener('submit', function (event) {
        var input = form.querySelector('[data-oh-search-input]');
        if (input && !input.value.trim()) {
          event.preventDefault();
          return;
        }
        // let the browser submit to routes.search_url
        form.setAttribute('action', searchUrl);
      });
    });

    /* ── mobile search row ──────────────────────────────── */

    if (msearchToggle && msearch) {
      msearchToggle.addEventListener('click', function () {
        var isOpen = msearch.classList.toggle('is-open');
        msearchToggle.setAttribute('aria-expanded', String(isOpen));
        if (isOpen) {
          var input = msearch.querySelector('[data-oh-search-input]');
          if (input) input.focus();
        } else if (openPanel === results) {
          hidePanel();
        }
      });
    }

    /* ── drawer ─────────────────────────────────────────── */

    function openDrawer() {
      if (!drawer) return;
      drawer.hidden = false;
      if (scrim) scrim.hidden = false;
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        if (scrim) scrim.classList.add('is-open');
      });
      document.body.style.overflow = 'hidden';
      root.querySelectorAll('[data-oh-drawer-open]').forEach(function (button) {
        button.setAttribute('aria-expanded', 'true');
      });
      var closeButton = drawer.querySelector('[data-oh-drawer-close]');
      if (closeButton) closeButton.focus();
    }

    function closeDrawer() {
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      if (scrim) scrim.classList.remove('is-open');
      document.body.style.overflow = '';
      root.querySelectorAll('[data-oh-drawer-open]').forEach(function (button) {
        button.setAttribute('aria-expanded', 'false');
      });
      window.setTimeout(function () {
        if (drawer.classList.contains('is-open')) return;
        drawer.hidden = true;
        if (scrim) scrim.hidden = true;
      }, 320);
    }

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-oh-drawer-open]')) {
        event.preventDefault();
        openDrawer();
        return;
      }
      if (event.target.closest('[data-oh-drawer-close]')) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      var accToggle = event.target.closest('[data-oh-acc-toggle]');
      if (accToggle) {
        var panel = accToggle.nextElementSibling;
        if (!panel || !panel.hasAttribute('data-oh-acc-panel')) return;
        var expanded = accToggle.getAttribute('aria-expanded') === 'true';
        accToggle.setAttribute('aria-expanded', String(!expanded));
        panel.classList.toggle('is-open', !expanded);
      }
    });

    /* ── hide while scrolling down ──────────────────
       Scrolling down slides the bar off the top; it comes back the moment
       scrolling stops, and immediately on any upward scroll. The section
       wrapper carries the class, not this element: the wrapper is what
       `position: sticky` is on (see the CSS in oneat-header.liquid).

       Everything the header can open - drawer, mobile search, submenus - is
       `position: fixed` or absolutely placed inside it, and the hidden state
       is a transform, which would become their containing block. Revealing on
       focusin and on any pointer press inside the header keeps the two states
       from ever overlapping. */

    if (root.dataset.ohHideOnScroll === 'true') {
      var section = root.closest('.shopify-section') || root;
      var IDLE_MS = 200;
      var JITTER = 6;
      var hidden = false;
      var idleTimer = null;
      var ticking = false;
      var hideY = 0;

      /* Horizon scrolls .page-wrapper rather than the document on some
         templates, so read whichever one is actually moving. */
      var wrapper = document.querySelector('.page-wrapper');
      var scrollTop = function () {
        return (wrapper && wrapper.scrollTop) || window.scrollY || 0;
      };

      /* The logo curve overhangs the bar, so the bar's own height is not far
         enough - the blob would still show above the top edge. */
      var measure = function () {
        var top = section.getBoundingClientRect().top;
        var bottom = section.getBoundingClientRect().bottom;
        var logo = root.querySelector('.oh-logo');
        if (logo) bottom = Math.max(bottom, logo.getBoundingClientRect().bottom);
        hideY = Math.ceil(bottom - top);
        section.style.setProperty('--oh-hide-y', hideY + 'px');
      };

      var apply = function (state) {
        if (state === hidden) return;
        hidden = state;
        section.classList.toggle('oh-hidden', state);
      };

      var reveal = function () {
        clearTimeout(idleTimer);
        apply(false);
      };

      var last = scrollTop();
      var onScroll = function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(function () {
            ticking = false;
            var y = scrollTop();
            var delta = y - last;
            // Never half-hidden at the top of the page.
            if (y <= hideY + 24) apply(false);
            else if (delta > JITTER) apply(true);
            else if (delta < -JITTER) apply(false);
            if (Math.abs(delta) > 2) last = y;
          });
        }
        // Back as soon as the page stops moving - this is the whole point.
        clearTimeout(idleTimer);
        idleTimer = setTimeout(function () { apply(false); }, IDLE_MS);
      };

      measure();
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(measure).observe(root);
      } else {
        window.addEventListener('resize', measure);
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });

      root.addEventListener('focusin', reveal);
      root.addEventListener('pointerdown', reveal, true);
    }

    /* ── --header-height ────────────────────────────────
       The inline script in layout/theme.liquid only publishes these
       when the stock `header-component` renders. With the stock
       header disabled nothing sets them, and the sticky add-to-cart
       offset (product-information-content.liquid) reads
       --header-height. Take over when the stock header is absent. */

    if (!document.querySelector('header-component')) {
      var publishHeight = function () {
        var height = root.offsetHeight;
        document.body.style.setProperty('--header-height', height + 'px');

        var group = document.querySelector('#header-group');
        var groupHeight = 0;
        if (group) {
          Array.prototype.forEach.call(group.children, function (child) {
            if (child !== root.parentElement && child instanceof HTMLElement) {
              groupHeight += child.offsetHeight;
            }
          });
        }
        document.body.style.setProperty('--header-group-height', groupHeight + 'px');
      };

      publishHeight();
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(publishHeight).observe(root);
      } else {
        window.addEventListener('resize', publishHeight);
      }
    }

    /* ── cart badge ─────────────────────────────────────── */

    var badge = root.querySelector('[data-oh-cart-count]');
    if (badge) {
      document.addEventListener('cart:update', function (event) {
        var cart = event.detail && event.detail.resource;
        if (!cart || typeof cart.item_count !== 'number') return;
        badge.textContent = String(cart.item_count);
        badge.hidden = cart.item_count === 0;
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-oneat-header]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // theme editor re-renders the section on every setting change
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-oneat-header]');
    if (root) init(root);
  });
})();
